import { Fragment, useLayoutEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { cn } from '@/lib/utils';
import { t } from '@/i18n';
import { actionCount, currentView, sectionOf, subViews } from './subViews';

// Mobile replacement for the sidebar sub-items: a scrollable row of chips above the content.
export const SubViewChips = () => {
  const { pathname } = useLocation();
  const base = sectionOf(pathname);
  const currentKey = base ? currentView(pathname, base).key : null;
  const navRef = useRef(null);
  const activeRef = useRef(null);

  // Keep the active chip visible. Scrolls only the row, never the page
  // (scrollIntoView would also move the page vertically).
  useLayoutEffect(() => {
    const nav = navRef.current;
    const chip = activeRef.current;
    if (!nav || !chip) return;
    const left = chip.offsetLeft - nav.offsetLeft;
    if (left < nav.scrollLeft || left + chip.offsetWidth > nav.scrollLeft + nav.clientWidth) {
      nav.scrollLeft = left - (nav.clientWidth - chip.offsetWidth) / 2;
    }
  }, [currentKey]);

  if (!base) return null;

  return (
    <nav ref={navRef} aria-label={t('nav.views')} className="lg:hidden -mx-8 px-8 mb-4 flex gap-2 overflow-x-auto pb-1">
      {subViews[base].map((v, i, views) => (
        <Fragment key={v.key}>
        {/* Setup views (import, sources) sit apart from the daily work. */}
        {v.group === 'setup' && views[i - 1]?.group !== 'setup' && (
          <span role="separator" aria-label={t('nav.planSetup')} className="shrink-0 self-stretch w-px bg-border mx-1" />
        )}
        <Link
          to={v.path}
          ref={v.key === currentKey ? activeRef : undefined}
          aria-current={v.key === currentKey ? 'page' : undefined}
          className={cn(
            'shrink-0 rounded-full border px-3 py-1.5 text-xs font-medium whitespace-nowrap',
            v.key === currentKey
              ? 'bg-primary text-primary-foreground border-primary'
              : 'bg-card text-muted-foreground hover:text-foreground',
          )}
        >
          {t(v.label)}
          {v.count && actionCount > 0 && (
            <span className="ml-1.5 rounded-full bg-red-600 px-1.5 text-[10px] font-bold text-white">{actionCount}</span>
          )}
        </Link>
        </Fragment>
      ))}
    </nav>
  );
};
